import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    FormControlLabel,
    Grid,
    Radio,
    Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useUser } from "../allauth/auth";
import { useRoleLabels } from "../hooks/useRoleLabels";
import ApiClient from "../config/ApiClient";
import type { AthletePaymentAssignment } from "../object-actions/types/types";
import { ROLE_CONFIG, USER_TYPE } from "../object-actions/types/types";
import { getRoleIcon } from "../utils/roleIcons";

interface Props {
    assignmentId: number;
    assignment?: AthletePaymentAssignment | null;
    onSuccess: () => void;
}

export default function RoleSelection({ assignmentId, assignment, onSuccess }: Props) {
    const { getUserTypeLabel } = useRoleLabels();
    const user = useUser();
    const [selectedRole, setSelectedRole] = useState<USER_TYPE | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const existingAthleteId = assignment?.athlete_id ?? assignment?.athlete?.id ?? null;
    const athleteSlotTakenByOther =
        existingAthleteId != null && Number(existingAthleteId) !== Number(user?.id);
    const athleteDisplayName = assignment?.athlete?.str ?? "another participant";

    const availableRoles = useMemo(() => {
        const roles: USER_TYPE[] = ["parent", "coach"];
        if (!athleteSlotTakenByOther) {
            roles.unshift("athlete");
        }
        return roles;
    }, [athleteSlotTakenByOther]);

    const handleSubmit = async () => {
        if (!selectedRole) {
            setError("Please select a role");
            return;
        }
        if (selectedRole === "athlete" && athleteSlotTakenByOther) {
            setError("Athlete slot is no longer available on this assignment.");
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const response = await ApiClient.post(
                `/api/payment-assignments/${assignmentId}/claim-role/`,
                { role: selectedRole },
            );

            if (!response.success) {
                const msg =
                    typeof response.error === "string"
                        ? response.error
                        : "Failed to save role selection. Please try again.";
                throw new Error(msg);
            }

            onSuccess();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to save role selection. Please try again.");
            setIsSubmitting(false);
        }
    };

    return (
        <Container
            maxWidth="md"
            sx={{ py: 4 }}
            data-attr="role_selection_screen"
            data-testid="role-selection-screen"
        >
            <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Choose Your Role
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Select how you will participate on this assignment
                </Typography>
                <Chip label="Payer" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                    Your payer role cannot be changed
                </Typography>
                {athleteSlotTakenByOther && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {athleteDisplayName} is already the athlete on this assignment, so that role is not available.
                    </Typography>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                {availableRoles.map((role) => {
                    const config = ROLE_CONFIG[role];
                    const isSelected = selectedRole === role;

                    return (
                        <Grid item xs={12} sm={6} key={role}>
                            <Card
                                data-attr={`role_selection_option--${role}`}
                                data-testid={`role-selection-option--${role}`}
                                sx={{
                                    height: "100%",
                                    cursor: "pointer",
                                    border: isSelected ? 2 : 1,
                                    borderColor: isSelected ? "primary.main" : "divider",
                                    "&:hover": { boxShadow: 3 },
                                }}
                                onClick={() => setSelectedRole(role)}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ textAlign: "center", mb: 2 }}>{getRoleIcon(role, "large")}</Box>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
                                        <FormControlLabel
                                            control={
                                                <Radio
                                                    checked={isSelected}
                                                    onChange={() => setSelectedRole(role)}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Typography variant="h6" component="h2">
                                                    {getUserTypeLabel(role)}
                                                </Typography>
                                            }
                                            sx={{ m: 0 }}
                                        />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                                        {config.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            <Box sx={{ textAlign: "center", mt: 4 }}>
                <Button
                    variant="contained"
                    size="large"
                    onClick={handleSubmit}
                    disabled={!selectedRole || isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                    data-attr="role_selection_continue"
                    data-testid="role-selection-continue"
                >
                    {isSubmitting ? "Saving..." : "Continue"}
                </Button>
            </Box>
        </Container>
    );
}
